import { useRef } from "react";
import type { GestureInput } from "../audio/types";
import type { Instrument } from "../data/instruments";
import { getKeyboardPerformanceMappings } from "../lib/keyboardPerformance";

interface MobileGesturePadProps {
  instrument: Instrument;
  onNoteStart: (gesture: GestureInput) => void;
  onNoteEnd: (noteName: string) => void;
  onControl: (controlId: string, value?: number | string | boolean) => void;
}

export function MobileGesturePad({ instrument, onNoteStart, onNoteEnd, onControl }: MobileGesturePadProps) {
  const pointersById = useRef(new Map<number, string>());
  const startXById = useRef(new Map<number, number>());
  const mappings = getKeyboardPerformanceMappings(instrument);

  return (
    <section className="mobile-gesture-pad" data-testid="mobile-gesture-pad" aria-label={`${instrument.name} mobile gesture pad`}>
      {mappings.map((mapping) => (
        <button
          key={mapping.key}
          type="button"
          className="mobile-gesture-cell"
          aria-label={`Touch ${instrument.name} ${mapping.role}`}
          onPointerDown={(event) => {
            try {
              event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              // Synthetic touch events in Playwright do not always own capture.
            }
            pointersById.current.set(event.pointerId, mapping.noteName);
            startXById.current.set(event.pointerId, event.clientX);
            onNoteStart({
              source: "pointer",
              noteName: mapping.noteName,
              velocity: 0.72,
              ornament: mapping.role.includes("right") ? "right_touch" : "left_touch",
              x: event.clientX,
              y: event.clientY,
              pressure: event.pressure
            });
          }}
          onPointerMove={(event) => {
            const startX = startXById.current.get(event.pointerId);

            if (startX === undefined) {
              return;
            }

            const dragAmount = Math.min(1, Math.abs(event.clientX - startX) / 140);

            if (instrument.id === "gayageum") {
              onControl("nonghyeon_depth", dragAmount);
            } else if (instrument.id === "oud" || instrument.id === "ney") {
              onControl("micro_bend_amount", dragAmount);
            } else if (instrument.id === "sitar") {
              onControl("sitar_meend_mode", dragAmount > 0.7 ? "expert" : dragAmount > 0.35 ? "standard" : "beginner");
            } else if (instrument.id === "gamelan_saron") {
              onControl("ombak_depth", dragAmount);
            }
          }}
          onPointerUp={(event) => {
            const noteName = pointersById.current.get(event.pointerId);
            if (noteName) {
              onNoteEnd(noteName);
            }
            pointersById.current.delete(event.pointerId);
            startXById.current.delete(event.pointerId);
          }}
          onPointerCancel={(event) => {
            const noteName = pointersById.current.get(event.pointerId);
            if (noteName) {
              onNoteEnd(noteName);
            }
            pointersById.current.delete(event.pointerId);
            startXById.current.delete(event.pointerId);
          }}
        >
          <kbd>{mapping.label}</kbd>
          <span>{mapping.role}</span>
        </button>
      ))}
    </section>
  );
}
